import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, DownloadIcon, ExternalLink, ArrowLeftRight } from "lucide-react";
import type { Document } from "@/types/documents";
import { downloadDocument } from "../services/documentService";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PDFRenderer } from "./PDFRenderer";

interface PdfCompareDialogProps {
  /** First document to compare */
  documentA: Document | null;
  /** Second document to compare */
  documentB: Document | null;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
}

/**
 * PdfCompareDialog - Side-by-side PDF comparison dialog
 *
 * Displays two PDF documents side by side for visual comparison.
 * Supports swapping positions, individual downloads, and opening in new tabs.
 */
export function PdfCompareDialog({
  documentA,
  documentB,
  open,
  onOpenChange,
}: PdfCompareDialogProps) {
  const [fileUrlA, setFileUrlA] = useState<string | null>(null);
  const [fileUrlB, setFileUrlB] = useState<string | null>(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [swapped, setSwapped] = useState(false);
  const { toast } = useToast();

  // Load document A
  useEffect(() => {
    const load = async () => {
      if (!open || !documentA) {
        if (fileUrlA?.startsWith("blob:")) URL.revokeObjectURL(fileUrlA);
        setFileUrlA(null);
        return;
      }

      if (fileUrlA?.startsWith("blob:")) URL.revokeObjectURL(fileUrlA);

      setLoadingA(true);
      try {
        const { data, error } = await supabase.storage
          .from("documents")
          .download(documentA.file_path);

        if (error) throw error;

        const blob = new Blob([data], { type: "application/pdf" });
        setFileUrlA(URL.createObjectURL(blob));
      } catch (error) {
        console.error("Error loading document A:", error);
        toast({
          title: "Error",
          description: `Failed to load ${documentA.filename}`,
          variant: "destructive",
        });
      } finally {
        setLoadingA(false);
      }
    };

    load();

    return () => {
      if (fileUrlA?.startsWith("blob:")) URL.revokeObjectURL(fileUrlA);
    };
  }, [open, documentA, toast]);

  // Load document B
  useEffect(() => {
    const load = async () => {
      if (!open || !documentB) {
        if (fileUrlB?.startsWith("blob:")) URL.revokeObjectURL(fileUrlB);
        setFileUrlB(null);
        return;
      }

      if (fileUrlB?.startsWith("blob:")) URL.revokeObjectURL(fileUrlB);

      setLoadingB(true);
      try {
        const { data, error } = await supabase.storage
          .from("documents")
          .download(documentB.file_path);

        if (error) throw error;

        const blob = new Blob([data], { type: "application/pdf" });
        setFileUrlB(URL.createObjectURL(blob));
      } catch (error) {
        console.error("Error loading document B:", error);
        toast({
          title: "Error",
          description: `Failed to load ${documentB.filename}`,
          variant: "destructive",
        });
      } finally {
        setLoadingB(false);
      }
    };

    load();

    return () => {
      if (fileUrlB?.startsWith("blob:")) URL.revokeObjectURL(fileUrlB);
    };
  }, [open, documentB, toast]);

  // Reset swap state when dialog closes
  useEffect(() => {
    if (!open) setSwapped(false);
  }, [open]);

  const handleDownload = async (doc: Document | null) => {
    if (!doc) return;

    try {
      const url = await downloadDocument(doc);
      if (url) {
        const link = window.document.createElement("a");
        link.href = url;
        link.download = doc.filename;
        link.setAttribute("target", "_blank");
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);

        toast({
          title: "Download Started",
          description: `Downloading ${doc.filename}`,
        });
      }
    } catch (error) {
      console.error("Download error:", error);
      toast({
        title: "Download Failed",
        description: "Could not download the file.",
        variant: "destructive",
      });
    }
  };

  const handleOpenNewTab = (fileUrl: string | null) => {
    if (fileUrl) window.open(fileUrl, "_blank");
  };

  // Determine which doc/url goes on which side based on swap state
  const leftDoc = swapped ? documentB : documentA;
  const rightDoc = swapped ? documentA : documentB;
  const leftUrl = swapped ? fileUrlB : fileUrlA;
  const rightUrl = swapped ? fileUrlA : fileUrlB;
  const leftLoading = swapped ? loadingB : loadingA;
  const rightLoading = swapped ? loadingA : loadingB;

  const renderPdfPanel = (
    doc: Document | null,
    fileUrl: string | null,
    loading: boolean,
    side: "left" | "right"
  ) => (
    <div className="flex h-full flex-1 flex-col overflow-hidden border-r last:border-r-0">
      {/* Panel header */}
      <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2">
        <span className="truncate text-sm font-medium" title={doc?.filename}>
          {doc?.filename || "No document"}
        </span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 hover:bg-neutral-200"
            onClick={() => handleDownload(doc)}
            disabled={!doc}
            title="Download"
          >
            <DownloadIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 hover:bg-neutral-200"
            onClick={() => handleOpenNewTab(fileUrl)}
            disabled={!fileUrl}
            title="Open in new tab"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* PDF content */}
      <div className="flex-1 overflow-auto">
        {loading && (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
        {!loading && fileUrl && doc && (
          <PDFRenderer
            docId={doc.id}
            blobUrl={fileUrl}
            onOpenInTab={() => handleOpenNewTab(fileUrl)}
            onDownload={() => handleDownload(doc)}
            filename={doc.filename}
          />
        )}
        {!loading && !fileUrl && (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            Failed to load PDF
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-h-[90vh] w-[95vw] max-w-[95vw] flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0 border-b pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base font-medium">Compare PDFs</DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSwapped(!swapped)}
              className="gap-2"
            >
              <ArrowLeftRight className="h-4 w-4" />
              Swap
            </Button>
          </div>
        </DialogHeader>

        <div className="flex flex-1 gap-1 overflow-hidden">
          {renderPdfPanel(leftDoc, leftUrl, leftLoading, "left")}
          {renderPdfPanel(rightDoc, rightUrl, rightLoading, "right")}
        </div>
      </DialogContent>
    </Dialog>
  );
}
